type PaymentProviderId = 'onestore' | 'pg';

type ProviderRuntimeConfig = {
  enabled: boolean;
  startUrl: string;
  provider?: string;
};

type PaymentRuntimeConfig = {
  onestore: ProviderRuntimeConfig;
  pg: ProviderRuntimeConfig;
};

type PaymentCopy = {
  selectTitle: string;
  selectMessage: string;
  onestoreTitle: string;
  onestoreReadyDesc: string;
  onestorePreparingDesc: string;
  otherTitle: string;
  otherReadyDesc: string;
  otherPreparingDesc: string;
  noticeTitle: string;
  providerPreparingMessage: string;
  onestorePreparingMessage: string;
  paymentUnavailableMessage: string;
  popupBlockedMessage: string;
  confirm: string;
  cancel: string;
  closeLabel: string;
};

const SELECTOR_DIALOG_ID = 'eddaba-payment-provider-dialog';
const NOTICE_DIALOG_ID = 'eddaba-payment-provider-notice';
const COPY_ELEMENT_ID = 'payment-provider-copy';
const PRODUCT_SELECTOR = '.charge-product-card[data-product-id]';

let runtimeConfigPromise: Promise<PaymentRuntimeConfig> | null = null;

function emptyRuntimeConfig(): PaymentRuntimeConfig {
  return {
    onestore: { enabled: false, startUrl: '' },
    pg: { enabled: false, startUrl: '', provider: '' },
  };
}

function readCopy(): PaymentCopy | null {
  const source = document.getElementById(COPY_ELEMENT_ID);
  if (!(source instanceof HTMLElement)) return null;

  const value = (name: keyof PaymentCopy): string => String(source.dataset[name] || '').trim();

  const copy: PaymentCopy = {
    selectTitle: value('selectTitle'),
    selectMessage: value('selectMessage'),
    onestoreTitle: value('onestoreTitle'),
    onestoreReadyDesc: value('onestoreReadyDesc'),
    onestorePreparingDesc: value('onestorePreparingDesc'),
    otherTitle: value('otherTitle'),
    otherReadyDesc: value('otherReadyDesc'),
    otherPreparingDesc: value('otherPreparingDesc'),
    noticeTitle: value('noticeTitle'),
    providerPreparingMessage: value('providerPreparingMessage'),
    onestorePreparingMessage: value('onestorePreparingMessage'),
    paymentUnavailableMessage: value('paymentUnavailableMessage'),
    popupBlockedMessage: value('popupBlockedMessage'),
    confirm: value('confirm'),
    cancel: value('cancel'),
    closeLabel: value('closeLabel'),
  };

  return Object.values(copy).every(Boolean) ? copy : null;
}

async function loadRuntimeConfig(): Promise<PaymentRuntimeConfig> {
  if (runtimeConfigPromise) return runtimeConfigPromise;

  runtimeConfigPromise = fetch('/api/public-config', {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  })
    .then(async (response) => {
      if (!response.ok) return emptyRuntimeConfig();
      const payload = await response.json().catch(() => ({})) as any;
      const paymentProviders = payload?.data?.paymentProviders || {};

      const normalize = (provider: any): ProviderRuntimeConfig => {
        const startUrl = String(provider?.startUrl || '').trim();
        return {
          enabled: Boolean(provider?.enabled) && startUrl !== '',
          startUrl,
          provider: String(provider?.provider || '').trim(),
        };
      };

      return {
        onestore: normalize(paymentProviders.onestore),
        pg: normalize(paymentProviders.pg),
      };
    })
    .catch(() => emptyRuntimeConfig());

  return runtimeConfigPromise;
}

function closeDialog(id: string): void {
  document.getElementById(id)?.remove();
}

function styleOverlay(element: HTMLElement): void {
  element.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483647',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'padding:24px',
    'background:rgba(0,0,0,.58)',
    'backdrop-filter:blur(4px)',
    '-webkit-backdrop-filter:blur(4px)',
  ].join(';');
}

function styleCard(element: HTMLElement): void {
  element.style.cssText = [
    'width:min(100%,390px)',
    'box-sizing:border-box',
    'padding:22px 20px 18px',
    'border:1px solid rgba(255,255,255,.09)',
    'border-radius:18px',
    'background:#1c1e22',
    'box-shadow:0 20px 60px rgba(0,0,0,.52)',
    'color:#fff',
    'font-family:inherit',
  ].join(';');
}

function showNotice(message: string, copy: PaymentCopy): void {
  closeDialog(SELECTOR_DIALOG_ID);
  closeDialog(NOTICE_DIALOG_ID);

  const previousActive = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const overlay = document.createElement('div');
  overlay.id = NOTICE_DIALOG_ID;
  overlay.setAttribute('role', 'presentation');
  styleOverlay(overlay);

  const dialog = document.createElement('div');
  dialog.setAttribute('role', 'alertdialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.setAttribute('aria-labelledby', 'eddaba-payment-notice-title');
  dialog.setAttribute('aria-describedby', 'eddaba-payment-notice-message');
  styleCard(dialog);
  dialog.style.textAlign = 'center';

  const title = document.createElement('h2');
  title.id = 'eddaba-payment-notice-title';
  title.textContent = copy.noticeTitle;
  title.style.cssText = 'margin:0;font-size:20px;font-weight:700;line-height:1.3;color:#fff';

  const body = document.createElement('p');
  body.id = 'eddaba-payment-notice-message';
  body.textContent = message;
  body.style.cssText = 'margin:14px 0 20px;font-size:14px;font-weight:400;line-height:1.5;color:#c8c8c8';

  const confirm = document.createElement('button');
  confirm.type = 'button';
  confirm.textContent = copy.confirm;
  confirm.style.cssText = [
    'width:100%',
    'min-height:46px',
    'border:0',
    'border-radius:12px',
    'background:#fa1155',
    'color:#fff',
    'font:inherit',
    'font-size:14px',
    'font-weight:700',
    'cursor:pointer',
  ].join(';');

  const close = () => {
    overlay.remove();
    previousActive?.focus();
  };

  confirm.addEventListener('click', close);
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) close();
  });
  overlay.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });

  dialog.append(title, body, confirm);
  overlay.append(dialog);
  document.body.append(overlay);
  confirm.focus();
}

function createProviderButton(
  titleText: string,
  descriptionText: string,
  enabled: boolean,
  onClick: () => void,
): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.style.cssText = [
    'width:100%',
    'min-height:76px',
    'display:flex',
    'align-items:center',
    'justify-content:space-between',
    'gap:14px',
    'padding:14px 16px',
    'border:1px solid rgba(255,255,255,.10)',
    'border-radius:14px',
    'background:#24262b',
    'color:#fff',
    'font:inherit',
    'text-align:left',
    'cursor:pointer',
  ].join(';');

  const textWrap = document.createElement('span');
  textWrap.style.cssText = 'display:flex;min-width:0;flex:1;flex-direction:column;gap:5px';

  const title = document.createElement('strong');
  title.textContent = titleText;
  title.style.cssText = 'font-size:15px;font-weight:700;line-height:1.35;color:#fff';

  const description = document.createElement('span');
  description.textContent = descriptionText;
  description.style.cssText = enabled
    ? 'font-size:13px;font-weight:400;line-height:1.35;color:#c8c8c8'
    : 'font-size:13px;font-weight:600;line-height:1.35;color:#9da3af';

  const chevron = document.createElement('span');
  chevron.textContent = '›';
  chevron.setAttribute('aria-hidden', 'true');
  chevron.style.cssText = 'font-size:26px;line-height:1;color:#9da3af';

  textWrap.append(title, description);
  button.append(textWrap, chevron);
  button.addEventListener('click', onClick);
  return button;
}

function isMobilePoc(): boolean {
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent)
    || window.matchMedia('(max-width: 767px)').matches;
}

function openProviderCheckout(
  provider: PaymentProviderId,
  productId: string,
  config: PaymentRuntimeConfig,
  copy: PaymentCopy,
): void {
  const target = config[provider];

  if (!target.enabled || !target.startUrl) {
    showNotice(
      provider === 'onestore' ? copy.onestorePreparingMessage : copy.providerPreparingMessage,
      copy,
    );
    return;
  }

  let checkoutUrl: URL;
  try {
    checkoutUrl = new URL(target.startUrl, window.location.origin);
  } catch {
    showNotice(copy.paymentUnavailableMessage, copy);
    return;
  }

  checkoutUrl.searchParams.set('productId', productId);
  checkoutUrl.searchParams.set('poc', isMobilePoc() ? 'POC_MOBILE' : 'POC_PC');

  closeDialog(SELECTOR_DIALOG_ID);

  if (isMobilePoc()) {
    window.location.assign(checkoutUrl.toString());
    return;
  }

  const popup = window.open(
    checkoutUrl.toString(),
    'eddaba-payment',
    'popup=yes,width=400,height=580,resizable=yes,scrollbars=yes',
  );

  if (!popup) showNotice(copy.popupBlockedMessage, copy);
}

async function showProviderSelector(productId: string): Promise<void> {
  const copy = readCopy();
  if (!copy) return;

  closeDialog(SELECTOR_DIALOG_ID);
  closeDialog(NOTICE_DIALOG_ID);

  const previousActive = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const config = await loadRuntimeConfig();

  const overlay = document.createElement('div');
  overlay.id = SELECTOR_DIALOG_ID;
  overlay.setAttribute('role', 'presentation');
  styleOverlay(overlay);

  const dialog = document.createElement('div');
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.setAttribute('aria-labelledby', 'eddaba-payment-provider-title');
  dialog.setAttribute('aria-describedby', 'eddaba-payment-provider-message');
  styleCard(dialog);

  const headingRow = document.createElement('div');
  headingRow.style.cssText = 'display:flex;align-items:flex-start;justify-content:space-between;gap:16px';

  const headingWrap = document.createElement('div');
  headingWrap.style.cssText = 'min-width:0;flex:1';

  const title = document.createElement('h2');
  title.id = 'eddaba-payment-provider-title';
  title.textContent = copy.selectTitle;
  title.style.cssText = 'margin:0;font-size:20px;font-weight:700;line-height:1.3;color:#fff';

  const message = document.createElement('p');
  message.id = 'eddaba-payment-provider-message';
  message.textContent = copy.selectMessage;
  message.style.cssText = 'margin:8px 0 0;font-size:14px;font-weight:400;line-height:1.45;color:#c8c8c8';

  headingWrap.append(title, message);

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.setAttribute('aria-label', copy.closeLabel);
  closeButton.textContent = '×';
  closeButton.style.cssText = [
    'width:36px',
    'height:36px',
    'display:grid',
    'place-items:center',
    'flex:0 0 auto',
    'border:0',
    'border-radius:50%',
    'background:transparent',
    'color:#d8d8d8',
    'font:inherit',
    'font-size:26px',
    'line-height:1',
    'cursor:pointer',
  ].join(';');

  headingRow.append(headingWrap, closeButton);

  const options = document.createElement('div');
  options.style.cssText = 'display:flex;flex-direction:column;gap:10px;margin-top:20px';

  const oneStore = createProviderButton(
    copy.onestoreTitle,
    config.onestore.enabled ? copy.onestoreReadyDesc : copy.onestorePreparingDesc,
    config.onestore.enabled,
    () => openProviderCheckout('onestore', productId, config, copy),
  );

  const otherPayment = createProviderButton(
    copy.otherTitle,
    config.pg.enabled ? copy.otherReadyDesc : copy.otherPreparingDesc,
    config.pg.enabled,
    () => openProviderCheckout('pg', productId, config, copy),
  );

  options.append(oneStore, otherPayment);
  dialog.append(headingRow, options);
  overlay.append(dialog);
  document.body.append(overlay);

  const close = () => {
    overlay.remove();
    previousActive?.focus();
  };

  closeButton.addEventListener('click', close);
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) close();
  });
  overlay.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });
  oneStore.focus();
}

function findProductCard(event: Event): HTMLElement | null {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
  for (const node of path) {
    if (node instanceof HTMLElement && node.matches(PRODUCT_SELECTOR)) return node;
  }

  const target = event.target;
  return target instanceof Element ? target.closest<HTMLElement>(PRODUCT_SELECTOR) : null;
}

function interceptProductSelection(event: Event): void {
  const card = findProductCard(event);
  if (!card) return;

  const chargeScreen = document.getElementById('charge-main-screen');
  if (!chargeScreen || chargeScreen.hidden) return;

  const productId = String(card.dataset.productId || '').trim();
  if (!productId) return;

  event.preventDefault();
  event.stopPropagation();
  if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
  void showProviderSelector(productId);
}

window.addEventListener('click', interceptProductSelection, true);
window.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  interceptProductSelection(event);
}, true);

// Warm the configuration cache after the session page loads. A failure safely
// leaves every provider disabled, so legacy PG code can never be reached.
window.addEventListener('eddaba:app-ready', () => {
  void loadRuntimeConfig();
});
